/**
 * Database Query Optimization Utilities
 * Helpers for writing efficient database queries and preventing common performance issues
 */

// ============================================================================
// QUERY BUILDER HELPERS
// ============================================================================

/**
 * Build optimized select clause with only required fields
 */
export const buildSelectClause = (fields = []) => {
  if (!fields?.length) return '*';
  return fields?.join(', ');
};

/**
 * Build efficient filter conditions
 */
export const buildFilterConditions = (filters = {}) => {
  const conditions = [];
  
  Object.entries(filters)?.forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      conditions?.push({ column: key, value });
    }
  });
  
  return conditions;
};

/**
 * Calculate optimal pagination parameters
 */
export const calculatePagination = (page = 1, pageSize = 20) => {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  return { from, to, limit: pageSize, offset: from };
};

// ============================================================================
// N+1 QUERY PREVENTION
// ============================================================================

/**
 * Detect potential N+1 query patterns
 */
export const detectN1Pattern = (queryLog = []) => {
  const patterns = [];
  const queryGroups = new Map();
  
  queryLog?.forEach((query) => {
    const baseQuery = query?.replace(/\d+/g, 'ID'); // Normalize IDs
    if (!queryGroups?.has(baseQuery)) {
      queryGroups?.set(baseQuery, []);
    }
    queryGroups?.get(baseQuery)?.push(query);
  });
  
  queryGroups?.forEach((queries, pattern) => {
    if (queries?.length > 5) {
      patterns?.push({
        pattern,
        count: queries?.length,
        severity: queries?.length > 20 ? 'high' : 'medium',
        suggestion: 'Consider using batch loading or joins',
      });
    }
  });
  
  return patterns;
};

/**
 * Batch loader factory to prevent N+1 queries
 */
export const createBatchLoader = (loadFn, options = {}) => {
  const { maxBatchSize = 100, batchDelay = 10 } = options;
  
  let batch = [];
  let batchTimeout = null;
  
  const executeBatch = async () => {
    if (batch?.length === 0) return;
    
    const currentBatch = [...batch];
    batch = [];
    
    try {
      const ids = currentBatch?.map((item) => item?.id);
      const results = await loadFn(ids);
      
      // Resolve all promises
      currentBatch?.forEach((item) => {
        const result = results?.find((r) => r?.id === item?.id);
        item?.resolve(result);
      });
    } catch (error) {
      currentBatch?.forEach((item) => item?.reject(error));
    }
  };
  
  return (id) => {
    return new Promise((resolve, reject) => {
      batch.push({ id, resolve, reject });
      
      if (batch.length >= maxBatchSize) {
        clearTimeout(batchTimeout);
        executeBatch();
      } else {
        clearTimeout(batchTimeout);
        batchTimeout = setTimeout(executeBatch, batchDelay);
      }
    });
  };
};

// ============================================================================
// QUERY CACHING
// ============================================================================

/**
 * Simple in-memory cache for query results
 */
class QueryCache {
  constructor(options = {}) {
    this.cache = new Map();
    this.ttl = options?.ttl || 60000; // Default 1 minute
    this.maxSize = options?.maxSize || 100;
  }
  
  get(key) {
    const item = this.cache?.get(key);
    if (!item) return null;
    
    if (Date.now() > item?.expiry) {
      this.cache?.delete(key);
      return null;
    }
    
    return item?.value;
  }
  
  set(key, value, customTtl = null) {
    // Evict oldest if at max size
    if (this.cache?.size >= this.maxSize) {
      const firstKey = this.cache?.keys()?.next()?.value;
      this.cache?.delete(firstKey);
    }
    
    this.cache?.set(key, {
      value,
      expiry: Date.now() + (customTtl || this.ttl),
    });
  }
  
  clear() {
    this.cache?.clear();
  }
  
  size() {
    return this.cache?.size;
  }
}

export const queryCache = new QueryCache({ ttl: 60000, maxSize: 100 });

/**
 * Cached query executor
 */
export const cachedQuery = async (cacheKey, queryFn, ttl = 60000) => {
  // Check cache first
  const cached = queryCache?.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  // Execute query
  const result = await queryFn();
  
  // Cache result
  queryCache?.set(cacheKey, result, ttl);
  
  return result;
};

// ============================================================================
// QUERY PERFORMANCE MONITORING
// ============================================================================

/**
 * Query performance tracker
 */
class QueryPerformanceTracker {
  constructor() {
    this.metrics = new Map();
  }
  
  track(queryName, duration, success = true) {
    if (!this.metrics?.has(queryName)) {
      this.metrics?.set(queryName, {
        count: 0,
        totalTime: 0,
        avgTime: 0,
        minTime: Infinity,
        maxTime: 0,
        errors: 0,
      });
    }
    
    const metric = this.metrics?.get(queryName);
    metric.count++;
    metric.totalTime += duration;
    metric.avgTime = metric?.totalTime / metric?.count;
    metric.minTime = Math.min(metric?.minTime, duration);
    metric.maxTime = Math.max(metric?.maxTime, duration);
    
    if (!success) {
      metric.errors++;
    }
  }
  
  getMetrics(queryName = null) {
    if (queryName) {
      return this.metrics?.get(queryName);
    }
    
    return Array.from(this.metrics?.entries())?.map(([name, metrics]) => ({
      name,
      ...metrics,
    }));
  }
  
  getSlowestQueries(limit = 10) {
    return this.getMetrics()?.sort((a, b) => b?.avgTime - a?.avgTime)?.slice(0, limit);
  }
  
  clear() {
    this.metrics?.clear();
  }
}

export const performanceTracker = new QueryPerformanceTracker();

/**
 * Wrapper to track query performance
 */
export const trackQueryPerformance = async (queryName, queryFn) => {
  const startTime = performance.now();
  let success = true;
  
  try {
    const result = await queryFn();
    return result;
  } catch (error) {
    success = false;
    throw error;
  } finally {
    const duration = performance.now() - startTime;
    performanceTracker?.track(queryName, duration, success);
    
    // Log slow queries
    if (duration > 500) {
      console.warn(`⚠️ Slow query: ${queryName} took ${duration?.toFixed(2)}ms`);
    }
  }
};

// ============================================================================
// CONNECTION POOLING HELPERS
// ============================================================================

/**
 * Check if connection pool is healthy
 */
export const checkConnectionHealth = async (supabase) => {
  try {
    const startTime = performance.now();
    const { error } = await supabase?.from('user_profiles')?.select('id')?.limit(1);
    const duration = performance.now() - startTime;
    
    return {
      healthy: !error,
      latency: duration,
      timestamp: new Date()?.toISOString(),
    };
  } catch (error) {
    return {
      healthy: false,
      error: error?.message,
      timestamp: new Date()?.toISOString(),
    };
  }
};

// ============================================================================
// QUERY OPTIMIZATION RECOMMENDATIONS
// ============================================================================

/**
 * Analyze query and provide optimization suggestions
 */
export const analyzeQuery = (query = {}) => {
  const suggestions = [];
  
  // Check for SELECT *
  if (query?.select === '*') {
    suggestions?.push({
      type: 'warning',
      message: 'Avoid SELECT * - specify only needed columns',
      impact: 'medium',
    });
  }
  
  // Check for missing indexes on filter columns
  if (query?.filters && Object.keys(query?.filters)?.length > 2) {
    suggestions?.push({
      type: 'info',
      message: 'Consider composite index for multiple filter columns',
      impact: 'high',
    });
  }
  
  // Check for large limit values
  if (query?.limit && query?.limit > 100) {
    suggestions?.push({
      type: 'warning',
      message: 'Large limit values can impact performance - consider pagination',
      impact: 'medium',
    });
  }
  
  // Check for missing pagination
  if (!query?.limit && !query?.range) {
    suggestions?.push({
      type: 'error',
      message: 'Missing pagination - always limit query results',
      impact: 'high',
    });
  }
  
  return suggestions;
};

export default {
  buildSelectClause,
  buildFilterConditions,
  calculatePagination,
  detectN1Pattern,
  createBatchLoader,
  queryCache,
  cachedQuery,
  performanceTracker,
  trackQueryPerformance,
  checkConnectionHealth,
  analyzeQuery,
};
function createQuery(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: createQuery is not implemented yet.', args);
  return null;
}

export { createQuery };
function batchFetch(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: batchFetch is not implemented yet.', args);
  return null;
}

export { batchFetch };